import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { SETTINGS_SECTIONS } from 'features/settings/config/SettingsSections';
import { SettingsSectionId } from 'features/settings/types/types';
import React, { createContext, useCallback, useState, PropsWithChildren } from 'react';

interface SettingsModalContextType {
  isModalOpen: boolean;
  modalTitle: string;
  modalTitleId: string;
  settingSectionId: SettingsSectionId;
  openModal: (sectionId: SettingsSectionId, sectionModalTitle: string) => void;
  onCloseModal: () => void;
}

export const SettingsModalContext = createContext<SettingsModalContextType>({
  isModalOpen: false,
  modalTitle: '',
  modalTitleId: '',
  settingSectionId: 'account',
  openModal: () => {},
  onCloseModal: () => {},
});

export const SettingsModalContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { translation } = useTranslationContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalTitleId, setModalTitleId] = useState('');
  const [settingSectionId, setSettingSectionId] = useState<SettingsSectionId>('account');

  const openModal = useCallback(
    (sectionId: SettingsSectionId, sectionModalTitle: string) => {
      const section = SETTINGS_SECTIONS.find((s) => s.id === sectionId);
      const sectionModalTitleId = `settings__${sectionModalTitle}-modal`;

      if (!section) {
        console.warn(`No settings section found for ID: ${sectionId}`);
      }
      setSettingSectionId(sectionId);
      setModalTitle(sectionModalTitle);
      setModalTitleId(sectionModalTitleId);
      setIsModalOpen(true);
    },
    [translation],
  );

  const onCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setModalTitle('');
    setModalTitleId('');
    setSettingSectionId('account');
  }, []);

  return (
    <SettingsModalContext.Provider
      value={{
        isModalOpen,
        modalTitle,
        modalTitleId,
        settingSectionId,
        openModal,
        onCloseModal,
      }}
    >
      {children}
    </SettingsModalContext.Provider>
  );
};
